import type { ImageName } from '../generated/images';

/** Filter categories. Home filters by residential/luxury/office/commercial; the
 *  portfolio page groups by sanctuary/office/dining. A project can have several. */
export type ProjectCategory = 'residential' | 'luxury' | 'office' | 'commercial' | 'dining';

export type ProjectImage = { image: ImageName; alt: string; caption: string };

export type Project = {
  slug: string;
  title: string;
  /** Small badge on cards, e.g. "Residential • Eco". */
  tag: string;
  categories: ProjectCategory[];
  /** One line for cards. */
  summary: string;
  image: ImageName;
  imageAlt: string;
  /** Facts shown on the portfolio page and the case study. Omit what isn't known. */
  facts?: { scale?: string; location?: string; completed?: string; discipline?: string };
  /** Shown on the homepage "Distinguished Case Studies" grid. */
  featuredOnHome?: boolean;
  /** Position on the /portfolio monograph list ("Monograph 01" …). */
  monograph: number;
  /** Project-type keyword appended to the case-study h1, e.g. "Biophilic Villa Interior". */
  typeLabel: string;
  /** Case-study narrative, one or more paragraphs per section. */
  narrative: { brief: string[]; approach: string[]; craft: string[]; outcome: string[] };
  /** Principal materials and finishes. */
  materials: string[];
  /** 2–3 supporting images for the case-study gallery. */
  gallery: ProjectImage[];
  /** Per-page SEO: title topic (~25–35 chars), 140–160 char description, 5–8 long-tail keywords. */
  seo: { title: string; description: string; keywords: string[] };
};

// NOTE: placeholder content from the Stitch design. Replace facts and images with real project data.
// The homepage six have no confirmed location or completion year — those facts are intentionally omitted.
export const projects: Project[] = [
  {
    slug: 'residence-at-palm-grove',
    title: 'Residence at Palm Grove',
    tag: 'Private Sanctuary',
    categories: ['residential', 'luxury'],
    summary:
      'Centralized around a skylit tropical atrium, this villa balances an indoor water reflection pool with endemic greenery and tactile sand-washed plaster, generating a cooled microclimate.',
    image: 'project-palm-grove-courtyard',
    imageAlt: 'Skylit interior courtyard of the Residence at Palm Grove with a reflection pool and tropical planting',
    facts: { scale: '6,800 sq ft', location: 'Coimbatore, TN', completed: '2024', discipline: 'Biophilic Architecture' },
    monograph: 1,
    typeLabel: 'Biophilic Villa Interior',
    narrative: {
      brief: [
        'The family wanted a home that stays calm and cool through long, humid summers without leaning entirely on air-conditioning — and one where every principal room looks onto something green.',
        'The existing plan had deep rooms with little cross-ventilation. The brief became a question of how to pull light, air and planting into the centre of the house.',
      ],
      approach: [
        'We organised the villa around a double-height skylit atrium. A shallow reflection pool sits at its base, with endemic palms and ferns planted directly into the ground plane so the courtyard reads as landscape rather than decoration.',
        'Living, dining and the stair all open onto this void. Warm air rises and escapes through high-level vents in the skylight, drawing cooler air across the water and through the rooms — a passive strategy suited to a hot, humid climate.',
      ],
      craft: [
        'Walls are finished in sand-washed lime plaster, which breathes and handles humidity better than conventional emulsion. Honed teak frames the openings, and the pool edge is cut from a single run of grey stone so the water appears to sit flush with the floor.',
        'Lighting is kept low and indirect, washing the plaster at night so the courtyard glows rather than glares.',
      ],
      outcome: [
        'The atrium has become the heart of daily life: a shaded, planted room that every space borrows light and air from, and a quiet backdrop that ages gracefully with the family.',
      ],
    },
    materials: ['Sand-washed lime plaster', 'Honed teak joinery', 'Grey stone pool coping', 'Ivory microcement floors', 'Endemic tropical planting'],
    gallery: [
      {
        image: 'services-residential-living',
        alt: 'Double-height living room with green sofas, a timber stair and full-height glazing onto the garden',
        caption: 'Living · Opening onto green',
      },
      {
        image: 'project-sanctuary-basin',
        alt: 'Hand-carved stone washbasin on a timber shelf against lime plaster with a bronze wall tap',
        caption: 'Detail · Carved stone basin',
      },
      {
        image: 'material-microcement',
        alt: 'Close-up of a hand-troweled ivory microcement surface',
        caption: 'Material · Hand-troweled finish',
      },
    ],
    seo: {
      title: 'Palm Grove Biophilic Villa Interior',
      description:
        'Case study: a biophilic villa interior built around a skylit atrium, reflection pool and lime plaster, designed to stay cool through hot, humid Indian summers.',
      keywords: [
        'biophilic villa interior design in Tamil Nadu',
        'skylit courtyard villa interior',
        'indoor reflection pool home design',
        'lime plaster walls for humid climates',
        'luxury villa interior designers in Coimbatore',
        'passive cooling interior design India',
      ],
    },
  },
  {
    slug: 'skyline-travertine-penthouse',
    title: 'Skyline Travertine Penthouse',
    tag: 'Private Sanctuary',
    categories: ['residential', 'luxury'],
    summary:
      'A serene aerie overlooking the urban basin. Features monolithic navona travertine cladding, structural cantilevered balconies, and architectural perimeter cove illumination.',
    image: 'project-skyline-penthouse',
    imageAlt: 'Penthouse living pavilion clad in travertine with cove lighting and city views',
    facts: { scale: '5,400 sq ft', location: 'Bengaluru, KA', completed: '2023', discipline: 'Spatial Turnkey & Stone' },
    monograph: 2,
    typeLabel: 'Luxury Penthouse Interior',
    narrative: {
      brief: [
        'A top-floor apartment with panoramic glazing on three sides. The owners asked for a home that felt grounded and quiet despite its height — somewhere the city view is enjoyed, not performed.',
      ],
      approach: [
        'We treated the penthouse as a single stone pavilion. A central travertine volume houses the fireplace, storage and services, freeing the perimeter for uninterrupted living space along the glass.',
        'Ceilings step down at the edges to conceal perimeter cove lighting, so the rooms are lit from their boundaries at dusk and the reflections in the glazing stay soft.',
      ],
      craft: [
        'Navona travertine was selected for its warm tone and quiet veining, and laid in large, carefully sequenced panels so the joints align across walls and consoles. Low cabinetry is finished in smoked oak with brushed brass detailing.',
        'All turnkey works — stone, joinery, lighting and soft furnishings — were coordinated by one team to keep tolerances tight between materials.',
      ],
      outcome: [
        'The result is a calm, monolithic interior that frames the skyline rather than competing with it, with a material palette chosen to wear in rather than wear out.',
      ],
    },
    materials: ['Navona travertine cladding', 'Smoked oak cabinetry', 'Brushed brass detailing', 'Perimeter LED cove lighting', 'Wool and linen upholstery'],
    gallery: [
      {
        image: 'project-travertine-oak-study',
        alt: 'Honed travertine panel meeting dark oak joinery with a fine brass inlay',
        caption: 'Detail · Travertine meets oak',
      },
      {
        image: 'journal-acoustic-great-room',
        alt: 'Open-plan great room with timber ceiling, soft seating and full-height windows',
        caption: 'Living · Soft perimeter light',
      },
      {
        image: 'home-after-living-room',
        alt: 'Living room with cove-lit ceiling, timber wall panels and green velvet armchairs',
        caption: 'Lounge · Layered illumination',
      },
    ],
    seo: {
      title: 'Skyline Travertine Penthouse',
      description:
        'Case study: a luxury penthouse interior with monolithic travertine cladding, smoked oak joinery and perimeter cove lighting, delivered turnkey by Green Valley.',
      keywords: [
        'luxury penthouse interior design India',
        'travertine stone cladding for living rooms',
        'turnkey apartment interiors in Bengaluru',
        'cove lighting design for penthouses',
        'high-end apartment interior designers',
        'stone and oak luxury living room',
      ],
    },
  },
  {
    slug: 'pinnacle-corporate-boardroom',
    title: 'Pinnacle Corporate Boardroom',
    tag: 'Executive Offices',
    categories: ['office', 'commercial'],
    summary:
      'Engineered acoustic geometry hidden behind fumed timber panels. Features a bespoke 22-foot single-pour engineered quartz conference plinth and invisible smart interfaces.',
    image: 'project-pinnacle-boardroom',
    imageAlt: 'Corporate boardroom with fumed timber acoustic panels and a long quartz conference table',
    facts: { scale: '3,200 sq ft', location: 'Chennai, TN', completed: '2024', discipline: 'Acoustic & Millwork' },
    monograph: 3,
    typeLabel: 'Executive Boardroom Interior',
    narrative: {
      brief: [
        'The leadership team needed a boardroom that works equally well for in-person negotiation and long video calls — with clear speech, no echo, and technology that stays out of sight until it is needed.',
      ],
      approach: [
        'We designed the room acoustically first. Slatted fumed timber panels line the walls, with absorptive layers concealed behind them, so the room sounds controlled without looking like a studio.',
        'All cabling runs in flush conduits to a single plinth-style conference table, keeping the table surface clean and the floor free of trailing wires. Screens, cameras and controls are integrated into the joinery.',
      ],
      craft: [
        'The 22-foot conference table is a single-pour engineered quartz top on a patinated bronze base, detailed so power and data rise discreetly through the plinth. The timber panels were fumed to deepen their tone evenly before installation.',
      ],
      outcome: [
        'A composed, quiet room that reflects the firm’s standards — designed so meetings are about the conversation, not the equipment.',
      ],
    },
    materials: ['Fumed oak acoustic slats', 'Engineered quartz table top', 'Patinated bronze base', 'Concealed acoustic absorbers', 'Charcoal leather seating'],
    gallery: [
      {
        image: 'services-corporate-boardroom',
        alt: 'Glass-walled boardroom with a long dark conference table and city views',
        caption: 'Boardroom · Integrated technology',
      },
      {
        image: 'about-teak-fluted-detail',
        alt: 'Fluted timber wall panel with a brass hinge detail',
        caption: 'Detail · Fluted timber panelling',
      },
      {
        image: 'material-reeded-glass',
        alt: 'Reeded glass partition framed in dark timber',
        caption: 'Material · Reeded glass screen',
      },
    ],
    seo: {
      title: 'Pinnacle Boardroom Interior',
      description:
        'Case study: an executive boardroom with fumed timber acoustic panelling, a 22-foot quartz conference table and concealed AV, designed for clear, quiet meetings.',
      keywords: [
        'corporate boardroom interior design Chennai',
        'acoustic timber wall panels for boardrooms',
        'executive office interior designers Tamil Nadu',
        'custom quartz conference table design',
        'concealed AV boardroom millwork',
        'premium office interiors South India',
      ],
    },
  },
  {
    slug: 'minimalist-primary-suite',
    title: 'Minimalist Primary Suite',
    tag: 'Private Sanctuary',
    categories: ['residential', 'luxury'],
    summary:
      'Sculpted for deep sensory restoration. Anchor points include rare bookmatched Italian Calacatta marble wall compositions, woven Belgian linens, and shadowless perimeter grazing.',
    image: 'project-primary-suite',
    imageAlt: 'Minimalist primary bedroom suite with a bookmatched marble feature wall and linen bedding',
    facts: { scale: '1,950 sq ft', location: 'Kochi, KL', completed: '2023', discipline: 'Master Suite Architecture' },
    monograph: 4,
    typeLabel: 'Luxury Master Bedroom Design',
    narrative: {
      brief: [
        'The owners wanted their bedroom, dressing room and bath to work as one restful suite — a place with nothing to distract the eye and everything within easy reach.',
      ],
      approach: [
        'We reduced the suite to a few strong elements. A full-height bookmatched marble wall forms the headboard; a low platform bed sits in front of it; storage disappears into flush joinery along the dressing corridor.',
        'Light grazes the walls from concealed perimeter channels, avoiding downlights over the bed, and sheer linen drapery filters the afternoon sun.',
      ],
      craft: [
        'The Calacatta slabs were bookmatched and dry-laid before fixing so the veining mirrors across the centreline. Joinery is finished in a soft matte lacquer with walnut interiors, and the textiles are natural woven linen chosen to stay comfortable in humid weather.',
      ],
      outcome: [
        'A quiet, tactile retreat where the materials do the work — designed to feel as calm at the end of a long day as it does on the first.',
      ],
    },
    materials: ['Bookmatched Calacatta marble', 'Woven linen textiles', 'Matte lacquer joinery', 'Walnut drawer interiors', 'Concealed perimeter lighting'],
    gallery: [
      {
        image: 'contact-marble-walnut',
        alt: 'White veined marble cabinet with walnut sides and a brass edge',
        caption: 'Detail · Marble and walnut',
      },
      {
        image: 'contact-walk-in-wardrobe',
        alt: 'Walk-in wardrobe with lit open shelving, green cabinetry and an arched window',
        caption: 'Dressing · Flush joinery',
      },
      {
        image: 'material-linen-silk',
        alt: 'Close-up of a woven linen throw over an upholstered chair',
        caption: 'Textile · Woven linen',
      },
    ],
    seo: {
      title: 'Minimalist Primary Suite Design',
      description:
        'Case study: a minimalist master bedroom suite with a bookmatched Calacatta marble wall, flush wardrobe joinery, woven linens and soft perimeter lighting.',
      keywords: [
        'luxury master bedroom interior design Kerala',
        'bookmatched marble bedroom feature wall',
        'minimalist bedroom suite design India',
        'walk-in wardrobe joinery design',
        'master suite interior designers in Kochi',
        'calm bedroom lighting design ideas',
      ],
    },
  },
  {
    slug: 'heritage-manor-dining',
    title: 'The Heritage Manor Dining',
    tag: 'Culinary & Dining',
    categories: ['dining', 'residential', 'luxury'],
    summary:
      'Reinterpreting classic Dravidian timber craft through contemporary restraint. Reclaimed plantation teak rafters, hand-hammered bronze lighting fixtures, and a 14-seat solid timber dining table.',
    image: 'project-heritage-manor-dining',
    imageAlt: 'Heritage manor dining hall with teak rafters, bronze pendant lights and a long timber table',
    facts: { scale: '4,100 sq ft', location: 'Nagercoil, TN', completed: '2022', discipline: 'Heritage Conservation & Dining' },
    monograph: 5,
    typeLabel: 'Heritage Home Dining Hall',
    narrative: {
      brief: [
        'An ancestral home with a tall, pitched dining hall that had lost much of its character to piecemeal repairs. The family wanted to gather around one long table again, in a room that honoured the house’s timber tradition.',
      ],
      approach: [
        'We stripped the hall back to its structure and reinstated the roof as the room’s main feature. Exposed rafters, a terracotta floor and limewashed walls let the volume speak, while a single long table anchors the space below.',
        'Lighting hangs low over the table so the high roof stays in soft shadow — intimate at dinner, airy by day.',
      ],
      craft: [
        'Rafters were renewed in reclaimed plantation teak, jointed in the traditional manner and oiled rather than varnished. The 14-seat table is solid teak, and the hand-hammered bronze pendants were made by local metalworkers to suit the scale of the room.',
      ],
      outcome: [
        'The hall is once again the centre of family life — a conservation-minded update that keeps the house’s Dravidian character while meeting the needs of a modern household.',
      ],
    },
    materials: ['Reclaimed plantation teak rafters', 'Solid teak dining table', 'Hand-hammered bronze pendants', 'Terracotta floor tiles', 'Limewashed walls'],
    gallery: [
      {
        image: 'material-teak',
        alt: 'Close-up of a traditional mortise-and-tenon joint in solid teak',
        caption: 'Craft · Traditional teak joinery',
      },
      {
        image: 'about-artisan-credenza',
        alt: 'Woodworker shaping a curved timber piece by hand in a workshop',
        caption: 'Atelier · Hand-finished timber',
      },
      {
        image: 'about-atelier-workshop',
        alt: 'Two craftsmen assembling timber components at a workbench',
        caption: 'Atelier · Joinery in progress',
      },
    ],
    seo: {
      title: 'Heritage Manor Dining Hall',
      description:
        'Case study: a heritage home dining hall in Nagercoil restored with reclaimed teak rafters, hand-hammered bronze lighting and a 14-seat solid teak dining table.',
      keywords: [
        'heritage home renovation in Nagercoil',
        'traditional teak dining hall interior',
        'Dravidian style home interior design',
        'reclaimed teak roof rafters restoration',
        'ancestral house interior designers Kanyakumari',
        'solid teak dining table for large families',
      ],
    },
  },
  {
    slug: 'high-precision-kitchen',
    title: 'High-Precision Kitchen',
    tag: 'Culinary & Dining',
    categories: ['dining', 'residential'],
    summary:
      'An ergonomic culinary laboratory with architectural purity. Features matte charcoal zero-glare soft-touch surfaces, continuous quartzite waterfall edges, and integrated smart extractors.',
    image: 'project-precision-kitchen',
    imageAlt: 'Modular kitchen with matte charcoal cabinetry and a quartzite waterfall island',
    facts: { scale: '1,250 sq ft', location: 'Madurai, TN', completed: '2024', discipline: 'Modular Joinery & Kitchens' },
    monograph: 6,
    typeLabel: 'Luxury Modular Kitchen Design',
    narrative: {
      brief: [
        'A family that cooks seriously and entertains often wanted a kitchen that works like a professional one but looks like part of the living space — clean surfaces, no clutter, nothing that shows fingerprints or grease.',
      ],
      approach: [
        'We planned the kitchen around clear work zones: preparation, cooking and cleaning, each with its own storage within arm’s reach. A large island holds the main prep surface and seating, with appliances tucked into tall housings and an appliance garage.',
        'Extraction is integrated and ducted to the outside — essential in a hot climate where heavy cooking otherwise warms the whole house.',
      ],
      craft: [
        'Cabinetry is a matte charcoal soft-touch laminate chosen for low glare and easy cleaning. The island is wrapped in quartzite with continuous waterfall edges, and the cooktop sits flush with the surface. Champagne-toned edge lighting picks out the worktops in the evening.',
      ],
      outcome: [
        'An efficient, calm kitchen that stays easy to clean after heavy daily use, and that reads as a piece of architecture when the cooking is done.',
      ],
    },
    materials: ['Matte charcoal soft-touch cabinetry', 'Quartzite waterfall island', 'Flush induction cooktop', 'Integrated ducted extraction', 'Champagne-tone LED edge lighting'],
    gallery: [
      {
        image: 'services-walnut-joinery',
        alt: 'Kitchen run with walnut cabinetry and a white marble backsplash',
        caption: 'Joinery · Walnut and stone',
      },
      {
        image: 'material-reeded-glass',
        alt: 'Reeded glass cabinet door framed in timber',
        caption: 'Detail · Reeded glass fronts',
      },
    ],
    seo: {
      title: 'High-Precision Modular Kitchen',
      description:
        'Case study: a luxury modular kitchen with matte charcoal cabinets, a quartzite waterfall island and ducted extraction, planned for serious daily home cooking.',
      keywords: [
        'luxury modular kitchen design in Madurai',
        'quartzite waterfall kitchen island',
        'matte charcoal kitchen cabinets India',
        'modular kitchen designers in Tamil Nadu',
        'kitchen with ducted chimney extraction',
        'ergonomic kitchen work zone planning',
      ],
    },
  },
  // Homepage case studies (slugs match the previous WordPress site's /portfolio/ URLs).
  {
    slug: 'eco-green-interior',
    title: 'Eco Green Interior',
    tag: 'Residential • Eco',
    categories: ['residential', 'luxury'],
    summary: 'Biophilic residential residence balancing nature and indoor air vitality.',
    image: 'project-eco-green-interior',
    imageAlt: 'Eco green interior with a living moss wall, indoor courtyard greenery and bamboo furniture',
    facts: { scale: 'Full residence', discipline: 'Biophilic Residential Design' },
    featuredOnHome: true,
    monograph: 7,
    typeLabel: 'Eco-Friendly Home Interior',
    narrative: {
      brief: [
        'The homeowners asked for an interior that feels connected to nature every day — fresh air, daylight and greenery inside the house — using materials that are kind to both the occupants and the environment.',
      ],
      approach: [
        'We brought planting indoors at several scales: a preserved moss feature wall, an internal courtyard garden and potted greenery positioned where daylight supports it. Rooms were arranged to encourage cross-ventilation so the home breathes naturally in humid weather.',
        'The colour palette follows the planting — soft greens, natural timber and warm neutrals — so the greenery feels part of the architecture rather than an addition to it.',
      ],
      craft: [
        'Furniture uses bamboo and responsibly sourced timber, finished with low-VOC coatings. Natural fibre textiles and breathable wall finishes help keep indoor air comfortable and reduce the build-up of damp.',
      ],
      outcome: [
        'A fresh, light-filled home where nature is part of everyday routine — designed to be healthy to live in and simple to maintain.',
      ],
    },
    materials: ['Preserved moss feature wall', 'Bamboo furniture', 'Low-VOC timber finishes', 'Natural fibre textiles', 'Breathable mineral wall finish'],
    gallery: [
      {
        image: 'services-residential-living',
        alt: 'Double-height living room with green sofas, a timber stair and garden views',
        caption: 'Living · A green palette',
      },
      {
        image: 'material-linen-silk',
        alt: 'Close-up of a natural woven linen throw',
        caption: 'Textile · Natural fibres',
      },
    ],
    seo: {
      title: 'Eco Green Biophilic Home Interior',
      description:
        'Case study: an eco-friendly home interior with a living moss wall, indoor courtyard planting, bamboo furniture and low-VOC finishes for healthier indoor air.',
      keywords: [
        'eco friendly home interior design Nagercoil',
        'biophilic home interiors in Tamil Nadu',
        'indoor moss wall design for homes',
        'low VOC interior finishes for houses',
        'green interior design with natural materials',
        'sustainable residential interior designers',
      ],
    },
  },
  {
    slug: 'modern-elegant-suite',
    title: 'Modern Elegant Suite',
    tag: 'Luxury Suite',
    categories: ['luxury', 'residential'],
    summary: 'High-comfort master quarters featuring integrated smart illumination.',
    image: 'project-modern-elegant-suite',
    imageAlt: 'Luxury master bedroom suite with upholstered wall panels and brushed gold pendant lamps',
    facts: { scale: 'Master suite', discipline: 'Master Suite Design' },
    featuredOnHome: true,
    monograph: 8,
    typeLabel: 'Modern Master Bedroom Interior',
    narrative: {
      brief: [
        'A master bedroom that should feel indulgent but restful, with lighting that adapts from bright morning routines to a soft glow at night — all controlled without hunting for switches.',
      ],
      approach: [
        'An upholstered wall panel frames the bed and softens the room acoustically. Lighting is layered — concealed cove light, bedside pendants and accent lights — and grouped into scenes on a smart lighting system so the mood changes with a single touch.',
        'Storage is built into the walls to keep floor space open and surfaces clear.',
      ],
      craft: [
        'Panels are upholstered in a durable woven fabric suited to humid conditions. Brushed gold pendants and hardware add warmth against a muted palette, and joinery is finished in a smooth matte lacquer.',
      ],
      outcome: [
        'A comfortable, elegant suite that feels hotel-like without losing the warmth of home, with lighting that supports both rest and routine.',
      ],
    },
    materials: ['Upholstered wall panelling', 'Brushed gold pendants', 'Smart scene lighting', 'Matte lacquer joinery', 'Linen bedding'],
    gallery: [
      {
        image: 'journal-limewash-bedroom',
        alt: 'Calm bedroom with limewashed walls, a low timber bed and sheer curtains',
        caption: 'Bedroom · Soft natural light',
      },
      {
        image: 'contact-walk-in-wardrobe',
        alt: 'Walk-in wardrobe with lit shelving and an upholstered bench',
        caption: 'Dressing · Built-in storage',
      },
      {
        image: 'material-linen-silk',
        alt: 'Close-up of a woven linen fabric on an upholstered chair',
        caption: 'Textile · Woven upholstery',
      },
    ],
    seo: {
      title: 'Modern Elegant Master Suite',
      description:
        'Case study: a modern master bedroom suite with upholstered wall panels, brushed gold pendants and smart scene lighting, designed for comfort and calm at home.',
      keywords: [
        'modern master bedroom interior design',
        'upholstered headboard wall panel design',
        'smart lighting for master bedrooms',
        'luxury bedroom interior designers Nagercoil',
        'master suite with walk-in wardrobe',
        'elegant bedroom design ideas India',
      ],
    },
  },
  {
    slug: 'executive-office-space',
    title: 'Executive Office Space',
    tag: 'Corporate Office',
    categories: ['office', 'commercial'],
    summary: 'Acoustically isolated corporate suites designed for executive clarity.',
    image: 'project-executive-office',
    imageAlt: 'Corporate boardroom with an oak conference table, leather chairs and linear LED lighting',
    facts: { scale: 'Executive floor', discipline: 'Corporate Office Interiors' },
    featuredOnHome: true,
    monograph: 9,
    typeLabel: 'Corporate Office Interior',
    narrative: {
      brief: [
        'A growing company needed executive cabins and a meeting room that offer privacy and focus while still feeling connected to the open workspace around them.',
      ],
      approach: [
        'Executive suites are acoustically separated with insulated partitions and sealed glazing, so conversations stay private while daylight still reaches the floor. A central meeting room uses linear LED lighting aligned with the table for even, glare-free light on faces and screens.',
        'Circulation is kept simple, with storage and services consolidated into joinery walls.',
      ],
      craft: [
        'An oak conference table and timber wall finishes bring warmth to the corporate palette. Leather seating, reeded glass screens and concealed cable management keep the rooms tidy and hard-wearing.',
      ],
      outcome: [
        'A focused, professional workplace where leaders can meet and think without interruption, and which presents the company well to visiting clients.',
      ],
    },
    materials: ['Oak conference table', 'Insulated acoustic partitions', 'Reeded glass screens', 'Linear LED lighting', 'Leather executive seating'],
    gallery: [
      {
        image: 'home-service-office',
        alt: 'Executive cabin with a timber desk, leather chair and glass partition',
        caption: 'Cabin · Private and daylit',
      },
      {
        image: 'services-corporate-boardroom',
        alt: 'Boardroom with a long conference table and floor-to-ceiling windows',
        caption: 'Meeting room · Even, glare-free light',
      },
      {
        image: 'material-reeded-glass',
        alt: 'Reeded glass partition in a dark timber frame',
        caption: 'Material · Reeded glass',
      },
    ],
    seo: {
      title: 'Executive Office Interior',
      description:
        'Case study: executive office interiors with acoustically isolated cabins, an oak boardroom table and glare-free linear LED lighting for focused, private work.',
      keywords: [
        'executive office interior design Nagercoil',
        'acoustic office cabin partitions',
        'corporate office interiors in Kanyakumari',
        'boardroom lighting design for offices',
        'office interior designers near Trivandrum',
        'premium executive cabin design ideas',
      ],
    },
  },
  {
    slug: 'classic-villa-interiors',
    title: 'Classic Villa Interiors',
    tag: 'Villa Heritage',
    categories: ['luxury', 'residential'],
    summary: 'Timeless craftsmanship infused with modern structural comforts.',
    image: 'project-classic-villa',
    imageAlt: 'Neoclassical villa foyer with arched hallways, marble flooring and chandeliers',
    facts: { scale: 'Villa', discipline: 'Villa Interiors' },
    featuredOnHome: true,
    monograph: 10,
    typeLabel: 'Classic Luxury Villa Interior',
    narrative: {
      brief: [
        'The owners loved classic architecture — arches, mouldings, marble — but wanted a home that lives like a modern one, with comfortable rooms, good light and up-to-date services.',
      ],
      approach: [
        'We kept the classical vocabulary in the formal spaces: arched hallways, a marble-floored foyer and chandeliers marking the main axis. Family rooms beyond are calmer and more relaxed, with the same proportions but lighter detailing.',
        'Modern comforts — concealed air-conditioning, layered lighting and generous built-in storage — were worked into the mouldings and joinery so they never interrupt the composition.',
      ],
      craft: [
        'Marble flooring was laid in a classic pattern with contrasting borders. Plaster mouldings and arches were detailed to suit the proportions of each room, and timber media walls and shelving add warmth to the living areas.',
      ],
      outcome: [
        'A gracious villa that feels timeless on arrival and easy to live in day to day — classic in spirit, modern in performance.',
      ],
    },
    materials: ['Patterned marble flooring', 'Plaster mouldings and arches', 'Crystal chandeliers', 'Walnut media wall', 'Concealed climate control'],
    gallery: [
      {
        image: 'about-hero-living-room',
        alt: 'Double-height living room with tall windows, built-in shelving and cream sofas',
        caption: 'Living · Generous proportions',
      },
      {
        image: 'journal-media-wall',
        alt: 'Living room with a walnut media wall, framed artworks and a low coffee table',
        caption: 'Family room · Walnut media wall',
      },
      {
        image: 'home-service-residential',
        alt: 'Warm living room with timber panelling, shelving and garden views',
        caption: 'Lounge · Warm timber detailing',
      },
    ],
    seo: {
      title: 'Classic Luxury Villa Interiors',
      description:
        'Case study: a classic luxury villa interior with arched hallways, patterned marble floors and chandeliers, discreetly updated with modern comforts and storage.',
      keywords: [
        'classic villa interior design in Nagercoil',
        'luxury villa interior designers Tamil Nadu',
        'marble foyer and arched hallway design',
        'neoclassical home interiors India',
        'villa living room with walnut media wall',
        'traditional luxury home interior ideas',
      ],
    },
  },
  {
    slug: 'modular-kitchen-concepts',
    title: 'Modular Kitchen Concepts',
    tag: 'Culinary Studio',
    categories: ['residential', 'dining'],
    summary: 'Precision German hardware married to ergonomic chef-grade layouts.',
    image: 'project-modular-kitchen',
    imageAlt: 'Modern modular kitchen with emerald green cabinetry and a white quartz waterfall island',
    facts: { scale: 'Kitchen & pantry', discipline: 'Modular Kitchens' },
    featuredOnHome: true,
    monograph: 11,
    typeLabel: 'Modular Kitchen Interior',
    narrative: {
      brief: [
        'A family kitchen that needed to handle everyday Indian cooking — heavy use, lots of utensils, plenty of storage — while looking bright and contemporary.',
      ],
      approach: [
        'We planned the layout around the cooking triangle, keeping sink, hob and refrigerator within easy reach. A quartz island adds prep space and casual seating, and tall units hold pantry storage and appliances.',
        'Drawers replace most base cupboards so pots and pans are easier to reach, with internal organisers for utensils and spices.',
      ],
      craft: [
        'Emerald green shutters bring colour and character, paired with a white quartz waterfall island that is easy to clean. Soft-close precision hardware and moisture-resistant carcasses are chosen to stand up to a humid coastal climate.',
      ],
      outcome: [
        'A colourful, practical kitchen that makes daily cooking easier and gives the family a natural place to gather.',
      ],
    },
    materials: ['Emerald green lacquered shutters', 'White quartz waterfall island', 'Soft-close precision hardware', 'Moisture-resistant carcasses', 'Under-cabinet LED lighting'],
    gallery: [
      {
        image: 'services-walnut-joinery',
        alt: 'Kitchen with walnut base cabinets and a white marble backsplash',
        caption: 'Joinery · Stone and timber',
      },
      {
        image: 'material-teak',
        alt: 'Close-up of a precise timber joint',
        caption: 'Detail · Precise joinery',
      },
      {
        image: 'about-atelier-workshop',
        alt: 'Craftsmen assembling cabinet components at a workbench',
        caption: 'Atelier · Built to measure',
      },
    ],
    seo: {
      title: 'Modular Kitchen Design Concepts',
      description:
        'Case study: a family modular kitchen with emerald green shutters, a white quartz waterfall island and soft-close hardware, planned for everyday Indian cooking.',
      keywords: [
        'modular kitchen designers in Nagercoil',
        'green modular kitchen cabinet ideas',
        'quartz island kitchen design India',
        'modular kitchen for Indian cooking',
        'kitchen interiors in Kanyakumari district',
        'moisture resistant kitchen cabinets coastal',
      ],
    },
  },
  {
    slug: 'gourmet-dining-atelier',
    title: 'Gourmet Dining Atelier',
    tag: 'Hospitality',
    categories: ['commercial', 'dining'],
    summary: 'Commercial destination engineered for ambiance and high patron turnover.',
    image: 'project-gourmet-dining',
    imageAlt: 'Fine dining restaurant with sculpted timber ceiling baffles and leather banquette booths',
    facts: { scale: 'Restaurant floor', discipline: 'Restaurant & Hospitality Interiors' },
    featuredOnHome: true,
    monograph: 12,
    typeLabel: 'Restaurant Interior Design',
    narrative: {
      brief: [
        'A restaurant that needed to feel special enough for celebrations yet work hard enough for busy service — with comfortable seating, a memorable atmosphere and efficient movement for staff.',
      ],
      approach: [
        'We zoned the floor into a mix of banquettes, booths and loose tables so the room suits couples, families and groups alike. Service routes were kept clear and short between kitchen and tables.',
        'Sculpted timber ceiling baffles define the dining zones, control noise and hide services, so the room stays lively without becoming loud.',
      ],
      craft: [
        'Leather banquettes, timber baffles and warm, dimmable lighting set the mood. Finishes were selected to be durable and easy to clean under heavy commercial use.',
      ],
      outcome: [
        'A warm, atmospheric dining room that guests remember and staff can run smoothly, night after night.',
      ],
    },
    materials: ['Sculpted timber ceiling baffles', 'Leather banquette seating', 'Dimmable warm lighting', 'Durable stone table tops', 'Hard-wearing commercial flooring'],
    gallery: [
      {
        image: 'services-boutique-restaurant',
        alt: 'Restaurant dining room with green velvet booths, timber ceiling beams and pendant lights',
        caption: 'Dining room · Mixed seating zones',
      },
      {
        image: 'home-service-commercial',
        alt: 'Restaurant with an arched entrance, a sculptural chandelier and terrazzo flooring',
        caption: 'Arrival · A memorable entrance',
      },
      {
        image: 'about-teak-fluted-detail',
        alt: 'Fluted timber wall detail with a brass fitting',
        caption: 'Detail · Fluted timber',
      },
    ],
    seo: {
      title: 'Gourmet Dining Restaurant Interior',
      description:
        'Case study: a fine dining restaurant interior with timber ceiling baffles, leather banquettes and zoned seating, designed for atmosphere and efficient service.',
      keywords: [
        'restaurant interior designers in Nagercoil',
        'fine dining restaurant interior design',
        'restaurant acoustic ceiling baffle design',
        'hospitality interiors in Tamil Nadu',
        'banquette seating restaurant layout',
        'cafe and restaurant interiors Trivandrum',
      ],
    },
  },
];

export const getProject = (slug: string) => projects.find((p) => p.slug === slug);

/** Projects in monograph order. */
export const projectsByMonograph = [...projects].sort((a, b) => a.monograph - b.monograph);
